import React from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import PostCard from './PostCard';

// Desktop layout components
const DesktopDayColumn = styled(Card)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    height: '100%',
}));

const DesktopDayHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
}));

const DesktopPostsContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    '&::-webkit-scrollbar': {
        width: 8,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.grey[100],
        borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.grey[400],
        borderRadius: 4,
        '&:hover': {
            backgroundColor: theme.palette.grey[600],
        },
    },
}));

// Mobile layout components
const MobileDaySection = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
}));

const MobileDayHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const MobilePostsContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    overflowX: 'auto',
    maxWidth: '100%',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
    '&::-webkit-scrollbar': {
        height: 6,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.grey[400],
        borderRadius: 3,
    },
    // Ensure cards don't shrink too much
    '& > *': {
        flexShrink: 0,
        minWidth: 'min(280px, calc(100vw - 32px))',
        maxWidth: 'min(320px, calc(100vw - 32px))',
    },
}));

/**
 * DayColumn Component
 * 
 * Displays a single day's posts with appropriate layout for mobile/desktop.
 * Handles chronological sorting and responsive design.
 * 
 * @param {Object} props
 * @param {string} props.date - Date string in YYYY-MM-DD format
 * @param {Array} props.posts - Array of posts for this day
 * @param {Function} props.onPostClick - Callback when a post is clicked
 * @param {boolean} props.isMobile - Whether to use mobile layout
 */
const DayColumn = ({ date, posts, onPostClick, isMobile }) => {
    const theme = useTheme();
    
    // Parse date and determine if it's today
    const dayObj = dayjs(date);
    const isToday = dayObj.isSame(dayjs(), 'day');
    
    // Sort posts chronologically
    const sortedPosts = [...posts].sort((a, b) => {
        const timeA = dayjs(a.created_at || a.scheduled_post_date);
        const timeB = dayjs(b.created_at || b.scheduled_post_date);
        return timeA.isBefore(timeB) ? -1 : 1;
    });

    // Render mobile layout
    if (isMobile) {
        return (
            <MobileDaySection elevation={0}>
                <MobileDayHeader>
                    <Box>
                        <Typography 
                            variant="h6" 
                            component="h3"
                            sx={{ 
                                fontWeight: isToday ? 'bold' : 'medium',
                                color: isToday ? 'primary.main' : 'text.primary'
                            }}
                        >
                            {dayObj.format('dddd')}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                        >
                            {dayObj.format('MMMM D, YYYY')}
                        </Typography>
                    </Box>
                    <Chip 
                        label={`${posts.length} post${posts.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                    />
                </MobileDayHeader>
                
                <MobilePostsContainer>
                    {sortedPosts.map(post => (
                        <PostCard
                            key={post.id}
                            post={post}
                            onClick={() => onPostClick(post)}
                            isMobile={true}
                        />
                    ))}
                </MobilePostsContainer>
            </MobileDaySection>
        );
    }

    // Render desktop layout
    return (
        <DesktopDayColumn elevation={0}>
            <DesktopDayHeader>
                <Typography 
                    variant="h6" 
                    component="h3"
                    sx={{ 
                        fontWeight: isToday ? 'bold' : 'medium',
                        color: isToday ? 'primary.main' : 'text.primary',
                        textAlign: 'center'
                    }}
                >
                    {dayObj.format('ddd')}
                </Typography>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ textAlign: 'center' }}
                >
                    {dayObj.format('MMM D')}
                </Typography>
                <Chip 
                    label={`${posts.length} post${posts.length !== 1 ? 's' : ''}`}
                    size="small"
                    variant="outlined"
                />
            </DesktopDayHeader>
            
            <DesktopPostsContainer>
                {sortedPosts.map(post => (
                    <PostCard
                        key={post.id}
                        post={post}
                        onClick={() => onPostClick(post)}
                        isMobile={false}
                    />
                ))}
            </DesktopPostsContainer>
        </DesktopDayColumn>
    );
};

export default DayColumn; 